<template>
  <div class="container">
    <div class="main-list-view">
      <div
        class="list-item"
        :class="{ 'is-featured': service.featured }"
        v-for="(service, index) in services"
        :key="index"
        :data-service-id="service.servicesID"
        @click="navigateToUser(service.servicesID, service.userID, service.servicesName, $event)"
        @mouseenter="onItemEnter(service, $event)"
        @mousemove="onItemMove"
        @mouseleave="hidePreview"
        @mousedown="hidePreview"
      >
        <!-- Left: Name + Badge + Description -->
        <div class="item-main">
          <div class="item-title-row">
            <span class="item-name" :title="service.servicesName || 'Untitled Service'">{{ service.servicesName || "Untitled Service" }}</span>
            <span class="item-badge">{{ service.serviceType1 || "Service" }}</span>
            <span
              v-if="isNewPost(service.createdTime)"
              class="item-new-badge"
              title="Posted in the last 24 hours"
            >
              NEW
            </span>
            <span v-if="service.featured" class="item-featured-badge" title="Featured listing">★ Featured</span>
          </div>
          <div class="item-desc" :title="service.Description || 'No description available.'">{{ service.Description || "No description available." }}</div>
        </div>

        <!-- Middle: Location + Posted time -->
        <div class="item-location">
          <div class="item-location-row">
            <img src="../icons/Frame 38.svg" alt="Location" class="loc-icon" />
            <span class="loc-text">{{
              service.city && service.country
                ? service.city + ", " + service.country
                : service.location || "N/A"
            }}</span>
          </div>
          <div v-if="service.createdTime" class="item-posted-row">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
            <span>{{ formatRelativeTime(service.createdTime) }}</span>
          </div>
        </div>

        <!-- Right: Actions -->
        <div class="item-actions" @click.stop>
          <span
            class="heart-icon"
            :class="{ favorited: service.favorites }"
            @click="toggleFavorite(service)"
            title="Add to favorites"
          >
            <svg width="20" height="20" viewBox="0 0 24 24">
              <path
                :fill="service.favorites ? 'red' : '#bbb'"
                d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42
                   4.42 3 7.5 3c1.74 0 3.41 0.81 4.5 2.09C13.09 3.81
                   14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4
                   6.86-8.55 11.54L12 21.35z"
              />
            </svg>
          </span>
          <span class="share-icon" @click="openShare(service)" title="Share">
            <img src="../icons/Actions.svg" alt="Share options" />
          </span>
          <button class="send-btn" @click="handleSendResponse(service, $event)">
            Send response
          </button>
        </div>
      </div>
    </div>

    <!-- Share Modal (advanced, per-service) -->
    <div v-if="showShare" class="share-overlay" @click.self="showShare = false">
      <div class="share-box">
        <button class="close-btn" @click="showShare = false">×</button>
        <h3>Share this Post</h3>
        <div class="share-options">
          <div class="share-item" @click.stop="shareWhatsApp(selectedService)">
            <i class="fab fa-whatsapp"></i>
            <span>WhatsApp</span>
          </div>
          <div class="share-item" @click.stop="shareTwitter(selectedService)">
            <i class="fab fa-twitter"></i>
            <span>Twitter (X)</span>
          </div>
          <div class="share-item" @click.stop="shareFacebook(selectedService)">
            <i class="fab fa-facebook-f"></i>
            <span>Facebook</span>
          </div>
          <div class="share-item" @click.stop="shareEmail(selectedService)">
            <i class="fas fa-envelope"></i>
            <span>Email</span>
          </div>
          <div class="share-item" @click.stop="shareInstagram(selectedService)">
            <i class="fab fa-instagram"></i>
            <span>Instagram</span>
          </div>
          <div class="share-item" @click.stop="copyLink(selectedService)">
            <i class="fas fa-link"></i>
            <span>Copy Link</span>
          </div>
        </div>
      </div>
    </div>

    <AuthModal
      v-if="showLogin"
      :mode="authMode"
      :service="selectedService"
      :actionType="loginActionType"
      @loginSuccess="handleLoginSuccess"
      @switchMode="switchMode"
      @close="closeLogin"
    />
    <FloatingView
      v-if="showFloating && selectedService"
      :service="selectedService"
      @close="closeFloating"
    />

    <!-- Dwell preview of the row's /view-post page. One instance for the whole
         list — it follows whichever row the cursor has settled on. -->
    <ViewPostPreview
      v-if="previewService && previewAnchor"
      :key="previewService.servicesID"
      :service="previewService"
      :anchor="previewAnchor"
    />
  </div>
</template>

<script>
import { trackFeaturedClick } from "@/utils/featuredPlacement";
import FloatingView from "../pages/SendResponse.vue";
import { WEB_ORIGIN } from "@/config/apiBase";
import axios from "axios";
import AuthModal from "../pages/Models/AuthModal.vue";
import ViewPostPreview from "../pages/ViewPostPreview.vue";
import { toast } from "vue-sonner";
import { formatRelativeTime, isNewPost } from "@/utils/relativeTime";
import { rememberListScroll } from "@/utils/listScrollMemory";
import { canonicalSlug } from "@/utils/slug";

// How long the cursor has to rest on a row before its preview opens. In the
// range hover previews normally use — Wikipedia and GitHub sit around
// 400-500ms — so it reads as a response to stopping, not as a timeout.
const PREVIEW_HOVER_DELAY = 600;
// How far the pointer may drift while the dwell runs. Past this it's still
// travelling, so the clock restarts: the card opens once the cursor settles,
// not merely once it has been over the row long enough.
const PREVIEW_SETTLE_RADIUS = 8;

export default {
  data() {
    return {
      // Row the preview is currently describing, and the viewport rect it was
      // anchored to. Both null = no preview on screen.
      previewService: null,
      previewAnchor: null,
      showFloating: false,
      selectedService: null,
      isvalid: false,
      authMode: "login",
      showLogin: false,
      showShare: false,
      selectedService: null,
      shareUrl: window.location.href,
      copiedLink: '',
      loginActionType: "",
      // Heart the user clicked while signed out — applied after they sign in.
      pendingFavorite: null,
    };
  },
  props: {
    services: {
      type: Array,
      required: true,
    },
  },
  components: {
    FloatingView,
    AuthModal,
    ViewPostPreview,
  },
  mounted() {
    this.verifyToken();
    // Scrolling or resizing moves the row out from under its anchored card, so
    // drop the preview rather than chase the row.
    // Capture, so a scroll inside any nested container counts too — scroll
    // events don't bubble.
    window.addEventListener("scroll", this.hidePreview, { passive: true, capture: true });
    window.addEventListener("resize", this.hidePreview);
    window.addEventListener("keydown", this.onPreviewKeydown);
  },
  beforeUnmount() {
    window.removeEventListener("scroll", this.hidePreview, { capture: true });
    window.removeEventListener("resize", this.hidePreview);
    window.removeEventListener("keydown", this.onPreviewKeydown);
    this.clearPreviewTimer();
  },
  // Views.vue keeps this component alive when switching to grid, so an
  // unmount hook alone would leave a stray card on screen.
  deactivated() {
    this.hidePreview();
  },
  methods: {
    // Expose relative time helpers to the template
    formatRelativeTime,
    isNewPost,

    // ── Dwell preview ──────────────────────────────────────────────────────
    // A pointer that can hover. Touch screens fire a synthetic mouseenter on
    // tap that never gets a matching mouseleave, which would strand the card.
    supportsHoverPreview() {
      return (
        typeof window !== "undefined" &&
        typeof window.matchMedia === "function" &&
        window.matchMedia("(hover: hover) and (pointer: fine)").matches
      );
    },

    onItemEnter(service, event) {
      if (!this.supportsHoverPreview()) return;

      // Entering a new row supersedes the previous one, so there is only ever
      // one pending timer and one card no matter how many rows are on screen.
      this.hidePreview();

      this._pendingRow = event.currentTarget;
      this._pendingService = service;
      this._cursorX = event.clientX;
      this._settleX = event.clientX;
      this._settleY = event.clientY;
      this.startPreviewTimer();
    },

    startPreviewTimer() {
      this.clearPreviewTimer();
      this._hoverTimer = setTimeout(this.openPreview, PREVIEW_HOVER_DELAY);
    },

    openPreview() {
      this._hoverTimer = null;

      const row = this._pendingRow;
      if (!row) return;

      // A modal opened during the dwell — don't stack a card on top of it.
      if (this.showShare || this.showLogin || this.showFloating) return;

      // Measure now, not at mouseenter: the row may have moved since (images
      // finishing, a re-render).
      const rect = row.getBoundingClientRect();
      // The listing's content block. Used only as the fallback centre when
      // there's no cursor x — the row itself is 1216px wide, so its midpoint
      // is nowhere near whatever the pointer was resting on.
      const content = row.querySelector(".item-main");
      const contentRect = content ? content.getBoundingClientRect() : null;

      this.previewAnchor = {
        top: rect.top,
        left: rect.left,
        right: rect.right,
        bottom: rect.bottom,
        width: rect.width,
        height: rect.height,
        cursorX: this._cursorX,
        fallbackX: contentRect
          ? contentRect.left + contentRect.width / 2
          : undefined,
      };
      this.previewService = this._pendingService;
    },

    // Track the cursor while a preview is pending: it decides where the card is
    // centred, and a pointer still in motion means the user hasn't settled.
    onItemMove(event) {
      if (!this._hoverTimer) return;
      this._cursorX = event.clientX;

      const dx = event.clientX - this._settleX;
      const dy = event.clientY - this._settleY;
      if (dx * dx + dy * dy > PREVIEW_SETTLE_RADIUS * PREVIEW_SETTLE_RADIUS) {
        this._settleX = event.clientX;
        this._settleY = event.clientY;
        this.startPreviewTimer();
      }
    },

    hidePreview() {
      this.clearPreviewTimer();
      this._pendingRow = null;
      this._pendingService = null;
      if (this.previewService) {
        this.previewService = null;
        this.previewAnchor = null;
      }
    },

    // WCAG 2.1 SC 1.4.13 wants hover content dismissible without moving the
    // pointer. The card itself stays pointer-events: none — letting the cursor
    // enter it would trigger the row's mouseleave and fight itself — so Escape
    // is the way out.
    onPreviewKeydown(event) {
      if (event.key === "Escape") this.hidePreview();
    },

    clearPreviewTimer() {
      if (this._hoverTimer) {
        clearTimeout(this._hoverTimer);
        this._hoverTimer = null;
      }
    },
    async toggleFavorite(service) {
      // Favorites belong to an account, so a guest's click can't be saved.
      // Rather than a bare alert, hold the click and open the sign-in modal
      // with favourites-specific copy — the heart is applied automatically
      // once they're in, so the click isn't lost.
      // Gate on the token, not on `isvalid` — the latter starts false and is
      // only resolved asynchronously by verifyToken(), so a signed-in user who
      // clicks the heart straight away would otherwise be shown the modal.
      if (!localStorage.getItem("auth-token")) {
        this.pendingFavorite = service;
        this.selectedService = service;
        this.authMode = "sendResponseLogin";
        this.loginActionType = "favorite";
        this.showLogin = true;
        localStorage.setItem("redirectTo", this.$route.fullPath);
        toast.info("Please sign in to save this to your favorites.");
        return;
      }
      await this.applyFavorite(service);
    },

    // The write itself, shared by a signed-in click and by the retry that
    // runs after a guest signs in.
    async applyFavorite(service) {
      const newStatus = !service.favorites;
      try {
        await axios.put("/api/services", {
          servicesID: service.servicesID,
          favorites: newStatus,
        });
        service.favorites = newStatus;
        toast[newStatus ? "success" : "info"](
          newStatus ? "Added to favorites!" : "Removed from favorites!"
        );
      } catch (err) {
        console.error("Failed to update favorite status", err);
        toast.error("Could not update favorites. Please try again.");
      }
    },

    // The template has always bound @loginSuccess to this, but the method was
    // missing — signing in from list view silently did nothing.
    async handleLoginSuccess(service) {
      this.showLogin = false;
      this.verifyToken();

      if (this.pendingFavorite) {
        const target = this.pendingFavorite;
        this.pendingFavorite = null;
        this.loginActionType = "";
        // verifyToken() resolves asynchronously; the token is already in
        // storage at this point, so trust it here rather than racing it.
        if (localStorage.getItem("auth-token")) await this.applyFavorite(target);
        return;
      }

      this.selectedService = service;
      this.showFloating = true;
    },
    verifyToken() {
      const token = localStorage.getItem("auth-token");
      // Guest (no token): asking the server would 401 with "Invalid or expired
      // token", which the global interceptor treats as an expired session and
      // shows the "session expired" banner to a user who never signed in.
      if (!token) {
        this.isvalid = false;
        return;
      }
      axios
        .post("/api/verify-token", {}, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        })
        .then(() => (this.isvalid = true))
        .catch(() => (this.isvalid = false));
    },
    switchMode(mode) {
      this.authMode = mode;
    },
    closeLogin() {
      this.showLogin = false;
    },
    closeAllPopups() {
      this.showFloating = false;
      this.showLogin = false;
    },
    handleSendResponse(service, event) {
      if (this.isvalid) {
        this.openFloating(service);
      } else {
        this.selectedService = service;
        this.authMode = "sendResponseLogin";
        this.loginActionType = event && event.type === "click" ? "" : "contact";
        this.showLogin = true;
        localStorage.setItem("redirectTo", this.$route.fullPath);
      }
    },
    goToBusinessProfile(service) {
      const userId = typeof service === 'string' ? service : service?.userID;
      if (!userId) return;
      const userName = typeof service === 'object' ? service?.userDetails?.userName : null;
      const slug = userName
        ? userName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
        : userId;
      this.$router.push({ name: "BusinessProfile", params: { slug } });
    },
    async navigateToUser(servicesID, userID, servicesName, event) {
      trackFeaturedClick((this.services || []).find((s) => s.servicesID === servicesID));
      // Capture the row's position in the list before any await — Views
      // restores it on Back-from-detail. See utils/listScrollMemory.js.
      rememberListScroll({
        path: (this.$route && this.$route.path) || "/",
        servicesID,
        el:
          event && event.target && event.target.closest
            ? event.target.closest("[data-service-id]")
            : null,
      });

      localStorage.setItem(
        "viewPostContext",
        JSON.stringify({ servicesID, userID, from: "/" })
      );
      // Stored slug verbatim (a "+N" duplicate-title suffix must survive);
      // fall back to deriving one from the title only if the lookup failed.
      let url = null;
      try {
        const res = await axios.post("/api/serviceslinklist", { serviceID: servicesID });
        if (res.data?.items?.[0]?.url) {
          url = res.data.items[0].url;
        }
      } catch (err) {
        console.warn("Error fetching service link:", err);
      }
      this.$router.push({
        name: "view-post",
        params: {
          serviceName: url || canonicalSlug(servicesName),
        },
      });
    },
    openFloating(service) {
      this.selectedService = service;
      this.showFloating = true;
    },
    closeFloating() {
      this.showFloating = false;
      this.selectedService = null;
    },
    async getServiceUrl(service) {
      if (!service) return window.location.href;
      let url = WEB_ORIGIN + "/view-post/";
      try {
        const res = await axios.post("/api/serviceslinklist", { serviceID: service.servicesID });
        if (res.data && res.data.items && res.data.items[0] && res.data.items[0].url) {
          url += res.data.items[0].url;
        } else {
          url += canonicalSlug(service.servicesName);
        }
      } catch (e) {
        url += canonicalSlug(service.servicesName);
      }
      return url;
    },
    async shareWhatsApp(service) {
      const url = await this.getServiceUrl(service);
      window.open(`https://wa.me/?text=${encodeURIComponent(url)}`, "_blank");
    },
    async shareTwitter(service) {
      const url = await this.getServiceUrl(service);
      window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}`, "_blank");
    },
    async shareFacebook(service) {
      const url = await this.getServiceUrl(service);
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, "_blank");
    },
    async shareEmail(service) {
      const url = await this.getServiceUrl(service);
      window.location.href = `mailto:?subject=Check this out&body=${encodeURIComponent(url)}`;
    },
    shareInstagram() {
      // Open the specified Instagram page directly
      window.open("https://www.instagram.com/renants_classifieds/", "_blank");
    },
    openShare(service) {
      this.selectedService = service;
      this.showShare = true;
    },
    async copyLink(service) {
      const url = await this.getServiceUrl(service);
      try {
        await navigator.clipboard.writeText(url);
        this.copiedLink = url;
        alert("Link copied to clipboard!");
      } catch (e) {
        alert("Failed to copy link");
      }
    },
  },
};
</script>

<style scoped>
.container {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 16px;
  width: 100%;
  max-width: 1280px;
  margin-bottom: 25px;
}

.main-list-view {
  font-family: "Inter", sans-serif;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  max-width: 1216px;
}

/* Single compact row */
.list-item {
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  padding: 12px 20px;
  width: 100%;
  background: #ffffff;
  border: 1px solid #e9eaeb;
  border-radius: 12px;
  box-shadow: 0px 1px 4px rgba(10, 13, 18, 0.06);
  cursor: pointer;
  transition: box-shadow 0.15s ease;
}

.list-item:hover {
  box-shadow: 0px 4px 12px rgba(10, 13, 18, 0.1);
}

/* Left: name + badge + desc */
.item-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.item-title-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.item-name {
  font-family: "Inter", sans-serif;
  font-weight: 600;
  font-size: 15px;
  color: #181d27;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 260px;
}

.item-badge {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 4px;
  border: 0.77px solid transparent;
  background: linear-gradient(white, white) padding-box,
    var(--color-primary-gradient) border-box;
  font-family: "Inter", sans-serif;
  font-weight: 600;
  font-size: 11px;
  line-height: 16px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 110px;
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
  -webkit-text-fill-color: transparent;
  background-image: var(--color-primary-gradient);
}

/* "NEW" badge — fresh-post signal (< 24h) */
.list-item.is-featured {
  border-color: #f59e0b;
  box-shadow: 0 0 0 1px #fcd34d, 0 2px 8px rgba(245, 158, 11, 0.16);
}

.item-featured-badge {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: linear-gradient(135deg, #d97706 0%, #f59e0b 100%);
  color: #ffffff;
  font-family: "Inter", sans-serif;
  font-weight: 700;
  font-size: 10px;
  letter-spacing: 0.04em;
  line-height: 14px;
  text-transform: uppercase;
  user-select: none;
}

.item-new-badge {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: linear-gradient(135deg, #16a34a 0%, #22c55e 100%);
  color: #ffffff;
  font-family: "Inter", sans-serif;
  font-weight: 700;
  font-size: 10px;
  letter-spacing: 0.06em;
  line-height: 14px;
  text-transform: uppercase;
  box-shadow: 0 1px 3px rgba(22, 163, 74, 0.35);
  user-select: none;
}

.item-desc {
  font-family: "Inter", sans-serif;
  font-size: 13px;
  font-weight: 400;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 420px;
}

/* Middle: location + posted-time, stacked */
.item-location {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 3px;
  width: 180px;
  min-width: 0;
}

.item-location-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 5px;
  min-width: 0;
  max-width: 100%;
}

.item-posted-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  font-family: "Inter", sans-serif;
  font-size: 11px;
  font-weight: 500;
  color: #8a8fa8;
  line-height: 1;
}

.item-posted-row svg {
  flex-shrink: 0;
  color: #9ca3af;
}

.loc-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.loc-text {
  font-family: "Inter", sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #0134a2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Right: actions */
.item-actions {
  flex-shrink: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
}

.heart-icon {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  transition: transform 0.1s;
}

.heart-icon:active {
  transform: scale(1.2);
}

.share-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: #ffffff;
  border-radius: 50%;
  cursor: pointer;
}

.share-icon img {
  width: 18px;
  height: 18px;
}

.send-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  background: #3467d5;
  border: 1px solid #ffffff;
  border-radius: 8px;
  box-shadow: 0px 1px 2px rgba(10, 13, 18, 0.05);
  font-family: "Inter", sans-serif;
  font-weight: 600;
  font-size: 14px;
  color: #ffffff;
  cursor: pointer;
  white-space: nowrap;
}

/* Share modal */
.share-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.share-box {
  position: relative;
  background: #fff;
  padding: 25px;
  width: 340px;
  border-radius: 18px;
  box-shadow: 0 5px 25px rgba(0, 0, 0, 0.15);
  animation: popupShow 0.25s ease;
}

@keyframes popupShow {
  from { opacity: 0; transform: scale(0.9); }
  to { opacity: 1; transform: scale(1); }
}

.share-box h3 {
  margin-bottom: 15px;
  font-size: 20px;
  font-weight: 600;
  text-align: center;
}

.close-btn {
  position: absolute;
  right: 5px;
  top: 5px;
  width: 32px;
  height: 32px;
  padding: 0;
  color: black;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  font-size: 20px;
  line-height: 32px;
  text-align: center;
  z-index: 10;
}

.share-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.share-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 10px;
  cursor: pointer;
  transition: 0.2s;
}

.share-item:hover {
  background: #f5f5f5;
  transform: translateY(-2px);
}

.share-item i {
  font-size: 24px;
  color: #333;
}

.share-item span {
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: #181d27;
}

/* Mobile: stack into 2 rows */
@media (max-width: 768px) {
  .list-item {
    flex-wrap: wrap;
    gap: 8px;
    padding: 12px 14px;
  }

  .item-main {
    flex: 1 1 100%;
    min-width: 0;
  }

  .item-name {
    max-width: 180px;
    font-size: 14px;
  }

  .item-desc {
    max-width: 100%;
  }

  .item-location {
    width: auto;
    flex: 1;
  }

  .item-actions {
    flex: 0 0 auto;
  }

  .send-btn {
    font-size: 13px;
    padding: 7px 12px;
  }
}

/* Tablet: 700–900px */
@media (min-width: 700px) and (max-width: 900px) {
  .item-name {
    max-width: 180px;
  }

  .item-desc {
    max-width: 200px;
  }

  .item-location {
    width: 140px;
  }
}
</style>
